import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { errorFromResponse } from '@/lib/errors';
import type { PopupAdminView, PopupRequest, PopupReservationView, CmsPage } from '../types/cms';

const BASE = '/admin/v1/popups';

const fetchPopups = async (page: number, size: number): Promise<CmsPage<PopupAdminView>> => {
  const q = new URLSearchParams({ page: String(page), size: String(size), sort: 'sortOrder,asc' });
  const res = await adminFetch(`${BASE}?${q}`);
  if (!res.ok) throw new Error(`popups list failed: ${res.status}`);
  const env = (await res.json()) as { data?: PopupAdminView[] | CmsPage<PopupAdminView> };
  const raw = env.data;
  if (Array.isArray(raw)) return { content: raw, totalElements: raw.length, totalPages: 1, number: 0, size };
  return raw ?? { content: [], totalElements: 0, totalPages: 0, number: 0, size };
};

const fetchPopup = async (popupId: string): Promise<PopupAdminView> => {
  const res = await adminFetch(`${BASE}/${popupId}`);
  if (!res.ok) throw new Error(`popup fetch failed: ${res.status}`);
  const env = (await res.json()) as { data?: PopupAdminView };
  if (!env.data) throw new Error('no data');
  return env.data;
};

const fetchPopupReservations = async (popupId: string, page: number, size: number): Promise<CmsPage<PopupReservationView>> => {
  const q = new URLSearchParams({ page: String(page), size: String(size) });
  const res = await adminFetch(`${BASE}/${popupId}/reservations?${q}`);
  if (!res.ok) throw new Error(`popup reservations fetch failed: ${res.status}`);
  const env = (await res.json()) as { data?: CmsPage<PopupReservationView> };
  return env.data ?? { content: [], totalElements: 0, totalPages: 0, number: 0, size };
};

export const popupsQueryKey = (page: number, size: number) =>
  ['admin', 'popups', page, size] as const;

export const popupQueryKey = (popupId: string) =>
  ['admin', 'popup', popupId] as const;

export const popupReservationsQueryKey = (popupId: string, page: number, size: number) =>
  ['admin', 'popup-reservations', popupId, page, size] as const;

export const usePopupsQuery = (page = 0, size = 20) =>
  useQuery({ queryKey: popupsQueryKey(page, size), queryFn: () => fetchPopups(page, size) });

/** QA-356 — 팝업 관리 검색은 화면에서 거르므로 전체를 받는다(이벤트 관리 fetchAllEvents 와 같은 방식). */
export const fetchAllPopups = async (): Promise<PopupAdminView[]> => {
  const SIZE = 500;
  const first = await fetchPopups(0, SIZE);
  const all = [...first.content];
  for (let p = 1; p < (first.totalPages ?? 1); p++) all.push(...(await fetchPopups(p, SIZE)).content);
  return all;
};

export const useAllPopupsQuery = () =>
  useQuery({ queryKey: ['admin', 'popups', 'all'] as const, queryFn: fetchAllPopups });

export const usePopupQuery = (popupId: string) =>
  useQuery({ queryKey: popupQueryKey(popupId), queryFn: () => fetchPopup(popupId), enabled: !!popupId && popupId !== 'new' });

export const usePopupReservationsQuery = (popupId: string, page = 0, size = 20) =>
  useQuery({
    queryKey: popupReservationsQueryKey(popupId, page, size),
    queryFn: () => fetchPopupReservations(popupId, page, size),
    enabled: !!popupId,
  });

export const usePopupMutation = () => {
  const qc = useQueryClient();
  // 수정 후 다시 열면 이전 값이 보이던 문제 — 목록 키만 무효화하고 단건 키(['admin', 'popup', id])는 그대로라
  // staleTime(30초) 안에는 캐시된 옛 값이 그대로 폼에 들어갔다. 단건 캐시도 함께 무효화한다.
  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['admin', 'popups'] }),
      qc.invalidateQueries({ queryKey: ['admin', 'popup'] }),
    ]);

  const create = useMutation({
    mutationFn: async (body: PopupRequest) => {
      const res = await adminFetch(BASE, { method: 'POST', body: JSON.stringify(body) });
      // 저장 실패는 BE error.code·message 를 살려 던진다(예: base64 이미지 400 사유를 그대로 안내).
      if (!res.ok) throw await errorFromResponse(res);
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ popupId, body }: { popupId: string; body: PopupRequest }) => {
      const res = await adminFetch(`${BASE}/${popupId}`, { method: 'PUT', body: JSON.stringify(body) });
      if (!res.ok) throw await errorFromResponse(res);
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (popupId: string) => {
      const res = await adminFetch(`${BASE}/${popupId}`, { method: 'DELETE',  });
      if (!res.ok) throw new Error(`delete failed: ${res.status}`);
    },
    onSuccess: invalidate,
  });

  return { create, update, remove };
};
